import React, { useState, useRef, useEffect, useContext } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { FaPaperPlane } from 'react-icons/fa';
import { AiOutlineMinusCircle, AiOutlinePlusCircle } from 'react-icons/ai';
import ProjectConfig from '../../constants';
import './chat.css';
import { analytics } from '../../services/analytics';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface Message {
	id: string;
	text: string;
	sender: 'user' | 'bot';
	timestamp: Date;
	isTyping?: boolean;
}

interface AIAssistantModalProps {
	isOpen: boolean;
	onClose: () => void;
	userId?: string;
}

const AIAssistantModal: React.FC<AIAssistantModalProps> = ({ isOpen, onClose, userId }) => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const [messages, setMessages] = useState<Message[]>([
		{
			id: '1',
			text: translate('chat.ai.welcomeMessage'),
			sender: 'bot',
			timestamp: new Date()
		}
	]);
	const [inputValue, setInputValue] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [isMinimized, setIsMinimized] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const modalContentRef = useRef<HTMLDivElement>(null);
	// 🔴 767px y no el `useIsMobile()` del repositorio, que corta en 639. El
	// `useBreakpointValue({ base: true, md: false })` de Chakra usaba SU breakpoint `md`, que son
	// 48em = 768px. Entre 640 y 767 el chat cambia de comportamiento —en móvil se cierra en vez
	// de minimizarse, y ocupa la pantalla entera—, así que usar el hook genérico habría movido
	// ese límite 128px sin que lo pidiera nadie.
	const isMobileScreen = useMediaQuery('(max-width: 767px)');
	const textAreaRef = useRef<HTMLTextAreaElement>(null);

	// Scroll al final de los mensajes cuando hay nuevos
	useEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	}, [messages]);

	// Función para ajustar altura automáticamente
	const autoResizeTextArea = () => {
		const textArea = textAreaRef.current;
		if (textArea) {
			textArea.style.height = 'auto';
			// 🔴 `scrollHeight` NO incluye los bordes, y ahora la caja es `border-box` con
			// `border` y `py-2`. Sin compensar, una sola línea deja el área de contenido 2px
			// corta y sale una barra de scroll permanente en el campo vacío. Con Chakra no
			// pasaba porque su `<Input as="textarea">` no traía padding vertical.
			const bordes = textArea.offsetHeight - textArea.clientHeight;
			const newHeight = Math.min(textArea.scrollHeight, 100); // Máximo 100px
			textArea.style.height = `${newHeight + bordes}px`;
		}
	};

	// Efecto para redimensionar el textarea cuando cambia el input
	useEffect(() => {
		autoResizeTextArea();
	}, [inputValue]);

	/**
	 * 🔴 Escape cierra TAMBIÉN con el chat plegado.
	 *
	 * Abierto lo resuelve Radix. Minimizado no hay diálogo, así que hace falta esto — y hace
	 * falta de verdad: en ese modo no hay botón de cerrar, solo de expandir, así que sin Escape
	 * el chat plegado es un callejón del que solo se sale abriéndolo otra vez. Con Chakra
	 * funcionaba porque el <Modal> seguía montado, minimizado incluido, y `closeOnEsc` va a true
	 * por defecto. Lo destapó la revisión.
	 */
	useEffect(() => {
		if (!isOpen || !isMinimized) return;
		const alPulsar = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', alPulsar);
		return () => document.removeEventListener('keydown', alPulsar);
	}, [isOpen, isMinimized, onClose]);

	// El clic fuera lo resuelve Radix con `onPointerDownOutside`, así que ya no hace falta el
	// listener de `mousedown` sobre el documento que había aquí. Aquel además se registraba
	// SIEMPRE, también con el chat cerrado.

	// Función para enviar mensaje al chatbot
	const sendMessage = async () => {
		if (!inputValue.trim()) return;

		// Registrar evento de métricas
		analytics.track('ai_assistant_message_sent');

		// Agregar mensaje del usuario
		const userMessage: Message = {
			id: Date.now().toString(),
			text: inputValue,
			sender: 'user',
			timestamp: new Date()
		};

		setMessages((prevMessages) => [...prevMessages, userMessage]);
		setInputValue('');
		setIsLoading(true);

		// Agregar mensaje de "escribiendo..." temporal
		const typingMsgId = `typing-${Date.now()}`;
		setMessages((prevMessages) => [
			...prevMessages,
			{
				id: typingMsgId,
				text: translate('chat.ai.typing'),
				sender: 'bot',
				timestamp: new Date(),
				isTyping: true // Usamos este flag para aplicar estilos especiales
			}
		]);

		try {
			// Llamada al backend
			const response = await fetch(`${process.env.REACT_APP_BASE_API}/latitude/prompt`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${localStorage.getItem('token')}`
				},
				body: JSON.stringify({ message: inputValue })
			});

			if (!response.ok) {
				throw new Error(translate('chat.ai.serverError'));
			}

			const result = await response.json();

			// Eliminar el mensaje de "escribiendo..."
			setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== typingMsgId));

			// Agregar respuesta del bot
			const botMessage: Message = {
				id: (Date.now() + 1).toString(),
				text:
					typeof result?.data?.response === 'string'
						? result.data.response
						: JSON.stringify(result?.data?.response) || translate('chat.ai.processingError'),
				sender: 'bot',
				timestamp: new Date()
			};

			setMessages((prevMessages) => [...prevMessages, botMessage]);

			// Si estaba minimizado, expandir para mostrar la respuesta
			if (isMinimized) {
				setIsMinimized(false);
			}
		} catch (error) {
			console.error(translate('chat.ai.communicationErrorLog'), error);

			// Eliminar el mensaje de "escribiendo..."
			setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== typingMsgId));

			// Mensaje de error
			const errorMessage: Message = {
				id: (Date.now() + 1).toString(),
				text: translate('chat.ai.communicationError'),
				sender: 'bot',
				timestamp: new Date()
			};

			setMessages((prevMessages) => [...prevMessages, errorMessage]);
		} finally {
			setIsLoading(false);
		}
	};

	// Enviar mensaje al presionar Enter
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !isLoading) {
			sendMessage();
		}
	};

	// Función para alternar entre minimizado y expandido
	const toggleMinimized = () => {
		if (isMobileScreen) {
			onClose(); // Cerrar modal completamente en móvil
		} else {
			setIsMinimized(!isMinimized);
		}
	};

	const colores = ProjectConfig.ARCA_COLORS;

	/**
	 * La barra de arriba, que es lo único común a los dos modos. Pulsarla alterna entre ellos —o
	 * cierra, en móvil— igual que antes.
	 */
	const cabecera = (
		<div
			className="flex cursor-pointer items-center justify-between rounded-t-md bg-primary px-4 text-primary-foreground"
			style={{
				paddingBlock: isMinimized ? '0.75rem' : '1rem',
				height: isMinimized ? '60px' : 'auto'
			}}
			onClick={toggleMinimized}
		>
			<p className={isMinimized ? 'text-sm' : 'text-base'}>{translate('chat.ai.title')}</p>

			{isMinimized && (
				<button
					type="button"
					aria-label={translate('chat.ai.expand')}
					onClick={(e) => {
						e.stopPropagation();
						toggleMinimized();
					}}
					className="flex size-8 items-center justify-center rounded-full transition-colors hover:bg-black/10 active:bg-black/20"
				>
					<AiOutlinePlusCircle size="18px" />
				</button>
			)}
		</div>
	);

	if (!isOpen) return null;

	/**
	 * 🔴 MINIMIZADO NO ES UN DIÁLOGO, y por eso el componente tiene dos ramas.
	 *
	 * Así estaba antes, aunque no lo pareciera: con Chakra, `blockScrollOnMount={!isMinimized}` y
	 * el overlay condicionado a `!isMinimized` dejaban la página usable con el ratón estando
	 * minimizado. Solo con el ratón: `trapFocus` y el `aria-hidden` del resto seguían puestos,
	 * así que con teclado o con lector de pantalla NO lo estaba. Aun así, un panel flotante.
	 *
	 * Meterlo en un `Dialog` de Radix «porque el otro modo lo es» atraparía el foco y bloquearía
	 * el scroll con el chat plegado — justo lo contrario de lo que significa minimizar.
	 */
	if (isMinimized) {
		return (
			<div
				ref={modalContentRef}
				className="fixed bottom-0 right-0 z-50 w-auto max-w-[90%] overflow-hidden rounded-t-md shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-all duration-300 md:bottom-5 md:right-5 md:w-[300px]"
			>
				{cabecera}
			</div>
		);
	}

	return (
		<Dialog.Root
			open
			// `modal`: con el chat abierto se bloquea el scroll y se atrapa el foco.
			//
			// Matiz que importa: `blockScrollOnMount` de Chakra solo hacía lo primero. El foco lo
			// atrapaba `trapFocus`, que seguía activo TAMBIÉN minimizado — o sea que el chat
			// plegado dejaba la página usable con el ratón pero no con el teclado ni con lector de
			// pantalla. Que ahora no lo haga es una mejora, no una equivalencia.
			modal
			onOpenChange={(abierto) => {
				if (!abierto) onClose();
			}}
		>
			<Dialog.Portal>
				{/*
				 * El velo va al 5 % y no al 80 % del Dialog de Proa. Es el valor que tenía
				 * (`rgba(0,0,0,0.05)`) y aquí importa: este panel se usa MIRANDO la pantalla de
				 * detrás —el asistente responde sobre lo que estás viendo—, así que oscurecerla
				 * sería un cambio de producto, no de estilo. Por eso se montan las primitivas de
				 * Radix en vez de `DialogContent`, que trae su propio velo y no deja cambiarlo:
				 * la alternativa era editar el componente del registry, y eso no se toca.
				 */}
				<Dialog.Overlay className="fixed inset-0 z-40 bg-black/5" />

				<Dialog.Content
					ref={modalContentRef}
					aria-describedby={undefined}
					// Escape cierra, que era el defecto de Chakra. Ojo: esto solo cubre el modo
					// ABIERTO; el plegado lo resuelve el efecto de arriba, porque ahí no hay diálogo.
					onEscapeKeyDown={onClose}
					// Y el clic fuera MINIMIZA, no cierra. Con Chakra lo hacía un listener de
					// `mousedown` sobre el documento que estaba registrado siempre, también con
					// el chat cerrado; ahora lo resuelve Radix y solo mientras está abierto.
					onPointerDownOutside={(e) => {
						e.preventDefault();
						setIsMinimized(true);
					}}
					className="fixed bottom-0 right-0 z-50 flex h-full w-full flex-col overflow-hidden bg-white shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-all duration-300 md:bottom-5 md:right-5 md:h-[500px] md:w-[400px] md:max-w-[450px] md:rounded-md"
				>
					{/* Radix exige un título accesible. Va oculto porque la cabecera ya lo enseña
					    —repetirlo lo haría leerse dos veces— pero sin él el diálogo se anuncia sin
					    nombre. */}
					<Dialog.Title className="sr-only">{translate('chat.ai.title')}</Dialog.Title>

					{cabecera}

					<div className="absolute right-2 top-2 z-10 flex">
						<button
							type="button"
							aria-label={translate('chat.ai.minimize')}
							onClick={(e) => {
								e.stopPropagation();
								// En móvil no hay sitio para un chat plegado: se cierra.
								if (isMobileScreen) onClose();
								else toggleMinimized();
							}}
							className="mr-2 flex size-8 items-center justify-center rounded-full text-primary-foreground transition-colors hover:bg-black/10 active:bg-black/20"
						>
							<AiOutlineMinusCircle size="18px" />
						</button>

						<Dialog.Close
							aria-label={translate('chat.ai.close')}
							className="flex size-8 items-center justify-center rounded-full text-primary-foreground transition-colors hover:bg-black/10 active:bg-black/20"
						>
							<span aria-hidden="true">✕</span>
						</Dialog.Close>
					</div>

					<div className="flex flex-1 flex-col overflow-y-hidden">
						{/* Los mensajes. El scrollbar fino va en CSS y no en `sx`, que era de Chakra. */}
						<div
							className="chat-scroll flex-1 overflow-y-auto p-4"
							style={{
								backgroundColor: colores.LIGHT_GREY_02,
								height: isMobileScreen ? 'calc(100vh - 180px)' : '350px'
							}}
						>
							{messages.map((message) => (
								<div
									key={message.id}
									className={`mb-4 flex items-start ${
										message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'
									}`}
								>
									{message.sender === 'bot' && (
										/*
										 * 🔴 Con `Avatar` y no con un `<img>`: `ai-assistant-icon.png`
										 * NO existe en `public/imgs`. El 404 es anterior a esta
										 * migración, pero el `Avatar` de Chakra lo tapaba con su
										 * icono por defecto y un `<img alt="">` roto deja un disco
										 * vacío. El fallback lo hace visible sin depender del asset.
										 */
										<Avatar className="mr-2 size-8 shrink-0">
											<AvatarImage src="/imgs/ai-assistant-icon.png" alt="" />
											<AvatarFallback className="text-xs">IA</AvatarFallback>
										</Avatar>
									)}

									<div
										className="max-w-[70%] break-words rounded-lg p-3 shadow-sm"
										style={{
											backgroundColor: message.sender === 'user' ? 'var(--primary)' : 'white',
											color: message.sender === 'user' ? 'var(--primary-foreground)' : 'black'
										}}
									>
										{message.isTyping ? (
											<div className="flex items-center">
												<p className="mr-2">{translate('chat.ai.typingMessage')}</p>
												<div
													className="chat-escribiendo"
													style={
														{
															'--chat-color-punto': 'var(--primary)'
														} as React.CSSProperties
													}
												>
													<span className="chat-escribiendo__punto" />
													<span className="chat-escribiendo__punto" />
													<span className="chat-escribiendo__punto" />
												</div>
											</div>
										) : (
											<p
												className={`whitespace-pre-wrap ${
													isMobileScreen ? 'text-sm' : 'text-base'
												}`}
											>
												{message.text}
											</p>
										)}
									</div>

									{message.sender === 'user' && (
										<Avatar className="ml-2 size-8 shrink-0">
											<AvatarImage src="/imgs/user-profile.png" alt="" />
											<AvatarFallback className="text-xs">Tú</AvatarFallback>
										</Avatar>
									)}
								</div>
							))}
							<div ref={messagesEndRef} />
						</div>

						{/* Escribir */}
						<div className="relative z-10 flex w-full items-end border-t border-gray-200 bg-white p-3">
							<div className="relative flex-1">
								<textarea
									ref={textAreaRef}
									placeholder={translate('chat.ai.placeholder')}
									value={inputValue}
									onChange={(e) => setInputValue(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey) {
											e.preventDefault();
											handleKeyDown(e);
										}
									}}
									disabled={isLoading}
									rows={1}
									className="chat-scroll min-h-10 max-h-[100px] w-full resize-none overflow-auto rounded-md border border-input px-3 py-2 pr-[50px] text-base focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
								/>

								<button
									type="button"
									aria-label={translate('chat.ai.sendMessage')}
									onClick={sendMessage}
									disabled={isLoading || !inputValue.trim()}
									className="absolute bottom-2 right-2 z-20 flex size-9 touch-manipulation select-none items-center justify-center rounded-full shadow-[0px_2px_4px_rgba(0,0,0,0.1)] transition-all duration-200 enabled:hover:scale-105 enabled:active:scale-95 disabled:cursor-not-allowed"
									style={{
										backgroundColor: inputValue.trim() ? 'var(--primary)' : 'var(--muted)',
										color: inputValue.trim()
											? 'var(--primary-foreground)'
											: 'var(--muted-foreground)',
										WebkitTapHighlightColor: 'transparent'
									}}
								>
									{isLoading ? (
										<span
											className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
											aria-hidden="true"
										/>
									) : (
										<FaPaperPlane />
									)}
								</button>
							</div>
						</div>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
};

export default AIAssistantModal;
